import FormControlLabel, {formControlLabelClasses, FormControlLabelProps} from '@mui/material/FormControlLabel';
import {alpha, styled} from '@mui/material/styles';
import MuiSwitch, {switchClasses, SwitchProps} from '@mui/material/Switch';
import {ElementType, forwardRef, ReactNode} from 'react';

type Variant = 'default' | 'dark' | 'staticDark';

type Props = Omit<FormControlLabelProps, 'control' | 'label'> & {
  variant?: Variant;
  switchProps?: SwitchProps;
  label?: ReactNode;
};

const SFormControlLabel = styled(FormControlLabel)(() => ({
  [`&.${formControlLabelClasses.root}`]: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const SSwitch = styled(MuiSwitch)(({theme}) => ({
  [`.${switchClasses.track}`]: {
    opacity: 1,
    backgroundColor: alpha(theme.palette.common.black, 0.26),
  },

  '&&': {
    [`.${switchClasses.checked}`]: {
      [`& + .${switchClasses.track}`]: {
        backgroundColor: theme.palette.text.primary,
        opacity: 1,
      },

      [`.${switchClasses.thumb}`]: {
        backgroundColor: theme.palette.common.white,
      },
    },
  },
}));

const SStaticDarkSwitch = styled(SSwitch)(({theme}) => ({
  [`.${switchClasses.track}`]: {
    opacity: 1,
    backgroundColor: theme.palette.text.primary,
  },
}));

const mapVariantToComponent: Record<Variant, ElementType> = {
  default: MuiSwitch,
  dark: SSwitch,
  staticDark: SStaticDarkSwitch,
};

const Switch = forwardRef(
  ({variant = 'default', switchProps, label = '', ...props}: Props, ref: React.ForwardedRef<HTMLLabelElement>) => {
    const SwitchComponent = mapVariantToComponent[variant];

    return <SFormControlLabel control={<SwitchComponent {...switchProps} />} {...props} label={label} ref={ref} />;
  }
);

export default Switch;
